"use client";

/** Selects the active account's SDK signer and routes delegated-account writes through the wallet. */

import {
  Ed25519Account,
  Ed25519PrivateKey,
  InputEntryFunctionData,
  InputGenerateTransactionPayloadData,
} from "@aptos-labs/ts-sdk";
import {
  DecibelWriteDex,
  GasPriceManager,
  getPrimarySubaccountAddr,
  getTradingCompetitionSubaccountAddr,
  submitAndWaitForTransaction,
  TransactionSettledMetrics,
  UserOwnedVault,
} from "@decibeltrade/sdk";
import { isServer, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";

import {
  SUBACCOUNT_ADDRESS_KEY_PREFIX,
  SUBACCOUNT_ADDRESS_LEGACY_KEY,
} from "@/constants/local-storage-keys";
import { env } from "@/env";
import { useMergedStorage } from "@/hooks/storage";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { useDelegatedSubaccounts } from "@/hooks/use-delegated-subaccounts";
import { useEncryptionPolicy } from "@/hooks/use-encryption-policy";
import { useGetImportedSessionKey } from "@/hooks/use-imported-session-keys";
import { useSessionKeys } from "@/hooks/use-session-keys";
import { useUserPreferencesByAccountAddress } from "@/hooks/use-user-preferences";
import { addressComparisonKey, addressesEqual } from "@/utils/address";
import { Logger } from "@/utils/logger";
import { getServerTimeDeltaSync, onServerTimeDeltaChange } from "@/utils/server-time";
import { generateGetAuthHeadersFunction, GetAuthHeaders } from "@/utils/signature-auth";
import { siwaDomain } from "@/utils/siwa-domain";

import { useAnalyticsRef } from "./analytics-context";
import { getSdkConfig } from "./dex-sdk";
import { useWalletFeePayerWithFallback } from "./mutations/wallet-fee-payer";
import { userSubaccountsQueryKey } from "./queries/user-subaccounts";
import { userOwnedVaultsQuery } from "./queries/vault/user-owned-vaults";
import { useViewAs } from "./view-as-context";

interface WriteDexSdkContextType {
  writeDexSdk?: DecibelWriteDex;
  /**
   * Whether writes from the active subaccount are submitted encrypted — read
   * straight off the SDK instance, so it can't drift from what is actually
   * submitted. Delegated subaccounts and vaults (incl. FFT locked funds) route
   * through the wallet in plaintext, so this is narrower than the device-level
   * `useEncryptionPolicy()`. With no write path it reports the device policy:
   * nothing is being submitted, so there's no plaintext submission to warn about.
   */
  writesEncrypted: boolean;
  subaccountAddress?: string;
  /** Connected wallet owner address. Used as a fee-query fallback before a subaccount exists. */
  ownerAddress?: string;
  primarySubaccountAddress?: string;
  connectedVault: UserOwnedVault | undefined;
  switchSubaccountAddress: (subaccountAddr: string) => void;
  delegateToSessionKey: () => Promise<void>;
  disconnect: () => void;
  cleanupAfterLogout: () => void;
  isLoading: boolean;
  clearSessionKeyForSubaccount: (accountAddress: string, subaccountAddr: string) => void;
  getAuthHeaders: GetAuthHeaders;
}
const WriteDexSdkContext = createContext<WriteDexSdkContextType | null>(null);

const sdkConfig = getSdkConfig();

const gasPriceManager = new GasPriceManager(sdkConfig, {
  refreshIntervalMs: 10 * 60_000,
  multiplier: 2,
  nodeApiKey: isServer ? env.SERVER_API_KEY : env.NEXT_PUBLIC_API_KEY,
});

const createWriteDexSdk = (
  sessionKey?: string,
  {
    defaultEncrypted = false,
    onTransactionSettled,
  }: {
    defaultEncrypted?: boolean;
    onTransactionSettled?: (metrics: TransactionSettledMetrics) => void;
  } = {},
): { writeDexSdk: DecibelWriteDex; sessionKey: string; accountInstance: Ed25519Account } => {
  if (!sessionKey) {
    const newPrivateKeyBytes = new Uint8Array(32);
    crypto.getRandomValues(newPrivateKeyBytes);
    sessionKey = Array.from(newPrivateKeyBytes).join(",");
  }

  const config = getSdkConfig();

  // Convert stored string back to Uint8Array
  const privateKeyArray = new Uint8Array(sessionKey.split(",").map(Number));
  const privateKey = new Ed25519PrivateKey(privateKeyArray);
  const accountInstance = new Ed25519Account({ privateKey });

  // Create the WriteDexSDK with server time delta
  const writeDexSdk = new DecibelWriteDex(config, accountInstance, {
    skipSimulate: true,
    gasPriceManager,
    timeDeltaMs: getServerTimeDeltaSync(),
    nodeApiKey: isServer ? env.SERVER_API_KEY : env.NEXT_PUBLIC_API_KEY,
    defaultEncrypted,
    onTransactionSettled,
  });

  return { writeDexSdk, sessionKey, accountInstance };
};

const zeroState = { writeDexSdk: undefined, subaccountAddress: undefined };

// Query keys carry whichever spelling their caller had; an undefined address still matches undefined parts.
function queryKeyMentions(queryKey: readonly unknown[], address: string | undefined): boolean {
  const key = addressComparisonKey(address);
  return queryKey.some(
    (part) => (typeof part === "string" ? addressComparisonKey(part) : part) === key,
  );
}

// Stable identity so the SDK isn't rebuilt when analytics loads; reports to the instance current at settle time.
function useTransactionSettledReporter(assigned: boolean) {
  const analyticsRef = useAnalyticsRef();
  const assignedRef = useRef(assigned);
  useLayoutEffect(() => {
    assignedRef.current = assigned;
  }, [assigned]);
  return useCallback(
    (metrics: TransactionSettledMetrics) => {
      analyticsRef.current.transaction.settled({
        ...metrics,
        network: env.NEXT_PUBLIC_SDK_CONFIG_NAME,
        assigned: assignedRef.current,
      });
    },
    [analyticsRef],
  );
}

export function WriteDexSdkContextProvider({ children }: PropsWithChildren) {
  const {
    isLoading,
    account,
    connected,
    disconnect: disconnectDecibelWallet,
    wallet,
    chainName,
  } = useDecibelWallet();
  const { getSessionKeyForAccount, setSessionKeyForAccount, clearSessionKeyForSubaccount } =
    useSessionKeys();
  const { isViewAsActive, viewAsSubaccountAddress, switchViewAsSubaccount } = useViewAs();

  const { mutateAsync: signAndSubmitWalletTransaction } = useWalletFeePayerWithFallback();
  const queryClient = useQueryClient();
  const accountAddress = account?.address.toString();
  const accountAddressRef = useRef(accountAddress);
  useLayoutEffect(() => {
    accountAddressRef.current = accountAddress;
  }, [accountAddress]);

  // Product-owned encryption decision. The SDK stays agnostic — it just submits
  // encrypted when told to (and still self-downgrades when the fullnode can't).
  const { shouldEncrypt, assigned } = useEncryptionPolicy();
  const onTransactionSettled = useTransactionSettledReporter(assigned);

  const [subaccountAddressToUse, setSubaccountAddressToUse] = useMergedStorage<string>(
    `${SUBACCOUNT_ADDRESS_KEY_PREFIX}${accountAddress ?? ""}`,
    "",
  );

  useEffect(() => {
    if (!accountAddress) return;
    sessionStorage.removeItem(SUBACCOUNT_ADDRESS_LEGACY_KEY);
    localStorage.removeItem(SUBACCOUNT_ADDRESS_LEGACY_KEY);
  }, [accountAddress]);

  const importedSessionKey = useGetImportedSessionKey(subaccountAddressToUse);
  const importedPrivateKey = importedSessionKey?.privateKey;
  const importedSubaccountAddress = importedSessionKey?.subaccountAddress;
  const delegatedSubaccounts = useDelegatedSubaccounts(accountAddress);

  const primarySubaccountAddress = useMemo(
    () =>
      connected && account
        ? getPrimarySubaccountAddr(
            account.address,
            sdkConfig.compatVersion,
            sdkConfig.deployment.package,
          )
        : undefined,
    [connected, account],
  );

  const { writeDexSdk, subaccountAddress } = useMemo<{
    writeDexSdk: DecibelWriteDex | undefined;
    subaccountAddress: string | undefined;
  }>(() => {
    // View-as mode: block all writes, use the viewed user's subaccount
    if (isViewAsActive) {
      return {
        writeDexSdk: undefined,
        subaccountAddress: viewAsSubaccountAddress || primarySubaccountAddress,
      };
    }

    const subaccountAddress = subaccountAddressToUse || primarySubaccountAddress;

    try {
      if (importedPrivateKey) {
        const privateKey = new Ed25519PrivateKey(importedPrivateKey);
        const { writeDexSdk } = createWriteDexSdk(privateKey.toUint8Array().join(","), {
          defaultEncrypted: shouldEncrypt,
          onTransactionSettled,
        });
        return { writeDexSdk, subaccountAddress: importedSubaccountAddress };
      }

      if (!accountAddress || !subaccountAddress) return zeroState;

      const sessionKey = getSessionKeyForAccount(accountAddress, subaccountAddress);

      if (sessionKey) {
        const { writeDexSdk } = createWriteDexSdk(sessionKey, {
          defaultEncrypted: shouldEncrypt,
          onTransactionSettled,
        });
        return { writeDexSdk, subaccountAddress };
      }

      const isDelegated = delegatedSubaccounts.some((v) =>
        addressesEqual(v.subaccountAddress, subaccountAddress),
      );
      if (isDelegated) {
        // Encryption bypasses sendTx, so disable it to keep delegated writes on the wallet path.
        // No telemetry: wallet writes would pollute the encrypted/plaintext comparison.
        const { writeDexSdk } = createWriteDexSdk(undefined, { defaultEncrypted: false });
        // Retain the hash if confirmation fails.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
        (writeDexSdk as any).sendTx = (payload: InputGenerateTransactionPayloadData) =>
          submitAndWaitForTransaction(writeDexSdk.aptos, () =>
            signAndSubmitWalletTransaction({
              data: payload as InputEntryFunctionData,
            }),
          );
        return { writeDexSdk, subaccountAddress };
      }

      return { writeDexSdk: undefined, subaccountAddress };
    } catch (error) {
      Logger.error(error, {
        message: "Failed to auto-hydrate WriteDexSDK",
        accountAddress: accountAddress?.toString(),
        subaccountAddr: subaccountAddress,
      });
      return { writeDexSdk: undefined, subaccountAddress: undefined };
    }
  }, [
    isViewAsActive,
    viewAsSubaccountAddress,
    importedPrivateKey,
    importedSubaccountAddress,
    accountAddress,
    subaccountAddressToUse,
    primarySubaccountAddress,
    getSessionKeyForAccount,
    delegatedSubaccounts,
    shouldEncrypt,
    onTransactionSettled,
    signAndSubmitWalletTransaction,
  ]);

  // Ask the SDK instance what it will do rather than tracking it alongside every
  // branch above. With no write path there's nothing to submit, so fall back to
  // the device policy instead of implying a plaintext submission.
  const writesEncrypted = writeDexSdk?.defaultEncrypted ?? shouldEncrypt;

  const getAuthHeaders = useMemo(() => {
    if (isServer) return undefined;
    // When the user arrives via an imported session key (no wallet connected
    // on this device), `accountAddress` from the wallet is undefined — fall
    // back to the imported key's owner so SIWA signing still works.
    const effectiveAccountAddress = accountAddress ?? importedSessionKey?.accountAddress;
    // TODO(server follow-up, DCBL-3410): backend's FRONTEND_DOMAIN check is
    // strict-equality against a single hostname, so any URL drift (apex vs
    // www, canonical vs Vercel alias, PWA install origin, mobile bookmark,
    // preview deploy, etc.) silently 401s. Proper fix: suffix-matched
    // allowlist server-side (e.g. FRONTEND_DOMAIN_SUFFIXES=decibel.trade,
    // decibel-git-*.vercel.app). Until that ships, we pin the client to a
    // canonical domain via NEXT_PUBLIC_SIWA_DOMAIN so every device signs the
    // same value, falling back to window.location.hostname when unset.
    return generateGetAuthHeadersFunction(
      writeDexSdk,
      effectiveAccountAddress,
      subaccountAddress,
      siwaDomain(),
    );
  }, [writeDexSdk, accountAddress, importedSessionKey?.accountAddress, subaccountAddress]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!window.gtag) return;
    try {
      if (accountAddress && subaccountAddressToUse) {
        const userProps = {
          owner_address: accountAddress,
          subaccount_address: subaccountAddressToUse,
          wallet_name: wallet?.name ?? "Unknown",
          chain: chainName,
          tc_participant: addressesEqual(
            subaccountAddressToUse,
            getTradingCompetitionSubaccountAddr(accountAddress),
          ),
        };

        window.gtag("set", "user_id", userProps.subaccount_address);
        window.gtag("set", "user_properties", userProps);
      } else {
        //  @ts-expect-error - user_id is optional
        window.gtag("set", "user_id", undefined);
      }
    } catch {
      // Ignore errors
    }
  }, [accountAddress, subaccountAddressToUse, chainName, wallet?.name]);

  // Update SDK's timeDeltaMs when server time delta changes
  useEffect(() => {
    if (!writeDexSdk) return;

    const unsubscribe = onServerTimeDeltaChange((delta) => {
      writeDexSdk.timeDeltaMs = delta;
    });

    return unsubscribe;
  }, [writeDexSdk]);

  const { data: userVaultsData } = useQuery({
    ...userOwnedVaultsQuery({ ownerAddr: accountAddress?.toString() ?? "" }),
    enabled: !!accountAddress,
  });

  const connectedVault = userVaultsData?.items.find((vault) =>
    addressesEqual(vault.subaccount_address, subaccountAddress),
  );

  const { clearLocalUserPreferences, updateDelegationsMetadata } =
    useUserPreferencesByAccountAddress(accountAddress, subaccountAddress, getAuthHeaders);

  useEffect(() => {
    if (!writeDexSdk) return;

    // Update SDK's timeDeltaMs when server time delta changes
    const unsubscribe = onServerTimeDeltaChange((delta) => {
      writeDexSdk.timeDeltaMs = delta;
    });

    // Initialize the gas price manager
    void gasPriceManager.initialize();

    return () => {
      unsubscribe();
      gasPriceManager.destroy();
    };
  }, [writeDexSdk]);

  const delegateToSessionKey = async (): Promise<void> => {
    if (isViewAsActive) {
      throw new Error("Cannot delegate session key in view-as mode");
    }
    if (!connected || !account) {
      throw new Error("Not connected to wallet");
    }
    const expectedAddress = addressComparisonKey(account.address.toString());
    if (addressComparisonKey(accountAddressRef.current ?? "") !== expectedAddress) {
      throw new Error("Wallet account changed — please retry");
    }

    // Create WriteDexSDK with new session key
    const { writeDexSdk, sessionKey, accountInstance } = createWriteDexSdk();
    const writeDexAddress = writeDexSdk.account.accountAddress.toString();

    if (!subaccountAddress) {
      throw new Error("Subaccount address not found");
    }

    let transactionData: InputEntryFunctionData;

    if (connectedVault) {
      const expiryTime = Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 365; // 1 year (maybe no expiration?)
      transactionData = {
        function: `${writeDexSdk.config.deployment.package}::vault_admin_api::delegate_dex_actions_to`,
        typeArguments: [],
        functionArguments: [connectedVault.vault_address, writeDexAddress, expiryTime],
      };
      const pendingTransaction = await signAndSubmitWalletTransaction({ data: transactionData });
      await writeDexSdk.aptos.waitForTransaction({
        transactionHash: pendingTransaction.hash,
      });
      if (addressComparisonKey(accountAddressRef.current ?? "") !== expectedAddress) {
        throw new Error("Wallet account changed during delegation — please retry");
      }

      setSessionKeyForAccount(account.address.toString(), subaccountAddress, sessionKey);
    } else {
      transactionData = {
        function: `${writeDexSdk.config.deployment.package}::dex_accounts_entry::delegate_trading_to_for_subaccount`,
        typeArguments: [],
        functionArguments: [subaccountAddress, writeDexAddress, undefined],
      };
      const pendingTransaction = await signAndSubmitWalletTransaction({ data: transactionData });
      await writeDexSdk.aptos.waitForTransaction({
        transactionHash: pendingTransaction.hash,
      });
      if (addressComparisonKey(accountAddressRef.current ?? "") !== expectedAddress) {
        throw new Error("Wallet account changed during delegation — please retry");
      }
      // Update the delegation metadata for the delegated account
      const delegatedAccountAddress = accountInstance.accountAddress.toString();
      void updateDelegationsMetadata({
        [delegatedAccountAddress]: {
          name: window.location.hostname,
          address: delegatedAccountAddress,
        },
      });
      setSessionKeyForAccount(account.address.toString(), subaccountAddress, sessionKey);
    }
  };

  const invalidateAccountQueries = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        queryKeyMentions(query.queryKey, accountAddress) &&
        (accountAddress
          ? query.queryKey !== userSubaccountsQueryKey({ ownerAddr: accountAddress })
          : true),
    });
  };

  const invalidateSubaccountQueries = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) => queryKeyMentions(query.queryKey, subaccountAddress),
    });
  };

  const switchSubaccountAddress = (subaccountAddr: string) => {
    if (isViewAsActive) {
      switchViewAsSubaccount(subaccountAddr);
      return;
    }
    setSubaccountAddressToUse(subaccountAddr);
  };

  /**
   * Post-logout cleanup shared by every sign-out path: invalidates cached
   * account/subaccount queries, clears the persisted subaccount selection, and
   * resets local user preferences. Does NOT touch wallet adapter state or
   * imported session keys — callers are responsible for the auth-specific
   * teardown before invoking this.
   */
  const cleanupAfterLogout = () => {
    void invalidateAccountQueries();
    void invalidateSubaccountQueries();
    setSubaccountAddressToUse("");
    clearLocalUserPreferences();
  };

  /**
   * Clean-up for the WALLET_CONNECTED auth path: disconnects the wallet adapter
   * then runs `cleanupAfterLogout`. Does NOT remove imported session keys, so
   * calling this while auth is SESSION_KEY_IMPORTED appears to do nothing —
   * `useGetImportedSessionKey` rehydrates from localStorage on next render.
   *
   * For UI that treats both connected states as "logged in" (e.g. a generic
   * Disconnect button), use `useLogout()` instead — it dispatches to the right
   * cleanup based on the current auth state.
   */
  const disconnect = () => {
    disconnectDecibelWallet();
    cleanupAfterLogout();
  };

  return (
    <WriteDexSdkContext.Provider
      value={{
        isLoading,
        writeDexSdk,
        writesEncrypted,
        primarySubaccountAddress,
        subaccountAddress,
        ownerAddress: accountAddress,
        switchSubaccountAddress,
        connectedVault,
        delegateToSessionKey,
        disconnect,
        cleanupAfterLogout,
        clearSessionKeyForSubaccount,
        getAuthHeaders,
      }}
    >
      {children}
    </WriteDexSdkContext.Provider>
  );
}

export function useWriteDexSdk() {
  const context = useContext(WriteDexSdkContext);
  if (!context) {
    throw new Error("useWriteDexSdk must be used within a WriteDexSdkContextProvider");
  }
  return context;
}

/**
 * `enabled` gate for React Query on endpoints hit via `authenticatedRequest`.
 * True once the SIWA session key has hydrated. Cookie-only users succeed
 * without it, so this is slightly stricter than necessary — but the gap
 * is transient and benign.
 */
export function useIsAuthReady(): boolean {
  return Boolean(useWriteDexSdk().getAuthHeaders);
}

/**
 * WriteDex signed by the session key delegated on the PRIMARY subaccount —
 * the FFT delegate check keys off primary, so the active subaccount's key
 * doesn't qualify. Undefined when no such key exists (wallet fallback).
 */
export function usePrimarySessionKeyDex(): DecibelWriteDex | undefined {
  const { account } = useDecibelWallet();
  const { primarySubaccountAddress } = useWriteDexSdk();
  const { getSessionKeyForAccount } = useSessionKeys();

  const accountAddress = account?.address.toString();

  return useMemo(() => {
    if (!accountAddress || !primarySubaccountAddress) return undefined;
    const sessionKey = getSessionKeyForAccount(accountAddress, primarySubaccountAddress);
    if (!sessionKey) return undefined;
    try {
      // Intentionally created without `defaultEncrypted`/`onTransactionSettled`:
      // this SDK only serves FFT campaign writes (open/claim/settle), which carry
      // no order intent and so aren't front-runnable — mempool privacy is moot.
      // Keeping them plaintext and out of `transaction_settled` also avoids
      // diluting the encrypted-rollout dashboards with a non-participating path.
      return createWriteDexSdk(sessionKey).writeDexSdk;
    } catch (error) {
      Logger.error(error, { message: "Failed to hydrate primary session-key WriteDexSDK" });
      return undefined;
    }
  }, [accountAddress, primarySubaccountAddress, getSessionKeyForAccount]);
}
